<script>
	import { onMount } from "svelte";
	import { fade } from "svelte/transition";
	import { postApi as post, putApi as put, delApi as del, getApi as get, patchApi as patch, refreshAiReportPromptCache } from "../../service/api";
	import { Dates } from "../../utils/date";
	import TableToolbar from "../../components/TableToolbar.svelte";
	import Pagination from "../../components/Pagination.svelte";
	import { menu, menuSub, menuSubSub } from "../../stores";
	import Tab from "../../components/Tab.svelte";

	// 상수 정의
	const TYPES = {
		deepSleep: { value: 1, text: "Deep Sleep" },
		powerNap: { value: 2, text: "Power Nap" },
		meditation: { value: 3, text: "Meditation" },
		focus: { value: 4, text: "Focus" },
	};

	const LANGUAGES = [
		{ field: "systemMessageKo", label: "시스템 메시지 (한국어)" },
		{ field: "systemMessageEn", label: "시스템 메시지 (영어)" },
		{ field: "systemMessageJa", label: "시스템 메시지 (일본어)" },
		{ field: "systemMessageZhCn", label: "시스템 메시지 (중국어간체)" },
		{ field: "systemMessageZhTw", label: "시스템 메시지 (중국어번체)" },
	];

	const RESPONSE_RULES = [
		{ field: "responseRulesKo", label: "응답 규칙 (한국어)" },
		{ field: "responseRulesEn", label: "응답 규칙 (영어)" },
		{ field: "responseRulesJa", label: "응답 규칙 (일본어)" },
		{ field: "responseRulesZhCn", label: "응답 규칙 (중국어간체)" },
		{ field: "responseRulesZhTw", label: "응답 규칙 (중국어번체)" },
	];

	const LENGTH_INSTRUCTIONS = [
		{ field: "lengthInstructionKo", label: "길이 지침 (한국어)" },
		{ field: "lengthInstructionEn", label: "길이 지침 (영어)" },
		{ field: "lengthInstructionJa", label: "길이 지침 (일본어)" },
		{ field: "lengthInstructionZhCn", label: "길이 지침 (중국어간체)" },
		{ field: "lengthInstructionZhTw", label: "길이 지침 (중국어번체)" },
	];

	// 상태 관리
	let activeTab = "all";
	let filteredData = [];
	let currentPage = 1;
	let totalPages = 1;
	let totalItems = 0;
	let pageSize = 10;
	const PAGE_SIZES = [10, 20, 50, 100];
	let data = [];

	// 모달 상태 관리
	let showModal = false;
	let showViewModal = false;
	let modalTitle = "";
	let viewModalTitle = "";
	let isSaving = false;

	// 편집 아이템 초기값
	const defaultEditItem = {
		id: 0,
		type: 1,
		systemMessageKo: "",
		systemMessageEn: "",
		systemMessageJa: "",
		systemMessageZhCn: "",
		systemMessageZhTw: "",
		responseRulesKo: "",
		responseRulesEn: "",
		responseRulesJa: "",
		responseRulesZhCn: "",
		responseRulesZhTw: "",
		lengthInstructionKo: "",
		lengthInstructionEn: "",
		lengthInstructionJa: "",
		lengthInstructionZhCn: "",
		lengthInstructionZhTw: "",
		isActive: false,
		engine: 1,
		creativity: 0.7,
		requestContent: "",
	};

	let editItem = { ...defaultEditItem };
	let viewItem = { ...defaultEditItem };

	// 메서드
	const setFocus = (field = "systemMessageKo") => {
		setTimeout(() => {
			const element = document.getElementById(field);
			if (element) {
				element.focus();
			}
		}, 100);
	};

	const validateItem = () => {
		// 시스템 메시지 검증
		for (const { field, label } of LANGUAGES) {
			if (!editItem[field]) {
				alert(`${label}을(를) 입력하세요.`);
				setFocus(field);
				return false;
			}
		}

		// 응답 규칙 검증
		for (const { field, label } of RESPONSE_RULES) {
			if (!editItem[field]) {
				alert(`${label}을(를) 입력하세요.`);
				setFocus(field);
				return false;
			}
		}

		// 길이 지침 검증
		for (const { field, label } of LENGTH_INSTRUCTIONS) {
			if (!editItem[field]) {
				alert(`${label}을(를) 입력하세요.`);
				setFocus(field);
				return false;
			}
		}

		return true;
	};

	const handleSave = async () => {
		if (!validateItem()) return;

		isSaving = true;
		try {
			const payload = {
				...editItem,
				type: parseInt(editItem.type),
				isActive: Boolean(editItem.isActive),
			};

			const success = editItem.id ? await put("/ai-report-prompt", payload) : await post("/ai-report-prompt", payload);

			if (success) {
				alert(editItem.id ? "프롬프트가 수정되었습니다." : "프롬프트가 등록되었습니다.");
				await fnSearch();
				closeModal();
			}
		} catch (error) {
			console.error("저장 실패:", error);
			alert("저장에 실패했습니다.");
		} finally {
			isSaving = false;
		}
	};

	const fnSearch = async () => {
		try {
			const response = await get(`/ai-report-prompt/${pageSize}/${currentPage}`);
			if (response && response.data) {
				data = response.data.list || [];
				totalItems = response.data.total || 0;
				totalPages = response.data.totalPage || 1;
			}
		} catch (error) {
			console.error("데이터 조회 실패:", error);
		}
	};

	const resetEdit = () => {
		editItem = { ...defaultEditItem };
	};

	const fnProcessRegist = () => {
		// 선택된 탭에 따라 type 설정
		let selectedType = 1; // 기본값: Deep Sleep
		if (activeTab !== "all") {
			selectedType = TYPES[activeTab]?.value || 1;
		}

		editItem = {
			...defaultEditItem,
			type: selectedType,
			systemMessageKo: "응답은 구어체 한국어(존대말)로 해주세요",
			systemMessageEn: "Please respond in conversational English.",
			systemMessageJa: "返答는会話調の日本語でお願いします。",
			systemMessageZhCn: "请用口语化的中文回答。",
			systemMessageZhTw: "請用口語化的中文回答。",
			lengthInstructionKo: "답변은 약 500자 이내로 긍정적인 내용으로 작성해 주세요.",
			lengthInstructionEn: "Keep your response positive and within approximately 110 words.",
			lengthInstructionJa: "返答はポジティブな内容で、350 文字以内にまとめてください。",
			lengthInstructionZhCn: "回答应积极正面，且尽量控制在 350 字以内。",
			lengthInstructionZhTw: "回答應積極正面，且儘量控制在 350 字以內。",
			engine: 2, // 기본값: GPT-4o
			creativity: 0.7,
		};

		// 분류별 기본 요청 내용 설정
		if (editItem.type === 1) {
			// Deep Sleep
			editItem.requestContent = `Start Time: "2025-03-12 00:02",
End Time: "2025-03-12 05:44",
Total sleep Time: "06:34",
Time in Bed (mins): 342,
Time to fall asleep (mins): 29,
Wake After Sleep Onset (mins): 40,
Sleep ratio REM/Light/Deep: "21/62/4",
Number of tosses and turns during sleep: 10,
Sleeping position ratio Supine/Left/Right: "65/18/17",
User Age: 46 (올해년도-출생년도)
User Gender: Male (Male/Female/Other)
HRV(RMSSD) avg by 5-minute intervals: "37,81,106,55,73,59,62,61,73,54,80,109,93,94,195,46,0,59,63,64,55,0,36,42,65,45,78,110,140,0",
Heart Rate min/avg/max by 5-minute intervals: "58/63/89,57/60/64,54/60/74,51/56/69,52/56/64,54/57/65,52/56/63,50/52/59,51/53/57,51/54/60,51/53/57,51/54/63,50/52/55,50/52/55,51/55/73,50/54/76,50/51/54,50/53/57,52/56/65,54/60/74,55/60/76,52/57/62,51/58/84,53/58/64,51/57/64,52/55/63,53/57/64,52/55/60,52/56/63,52/57/66,51/53/60,51/57/73,51/58/81,50/53/67,50/53/59,51/56/84,50/52/56,50/53/60,52/56/66,50/53/62,50/52/55,51/61/81,50/61/99,50/55/75,50/54/71,50/52/62,51/60/77,0/28/81,0/43/71,50/52/56,50/56/75,51/58/82,50/56/71,50/54/59,50/53/59,51/57/64,51/57/85,51/54/68,50/54/62,50/53/58,51/55/63,51/57/66,0/5/59,0/37/78,0/17/86,50/59/94,0/38/56,51/58/76,50/52/57,50/52/56,51/52/58,50/52/56,50/51/55,50/51/54,50/52/55,0/48/62,0/22/81,0/15/54,0/42/66,0/40/71,50/56/64,50/55/66,51/62/94,50/57/75,50/57/77,50/53/64,51/53/57,0/21/70,0/34/65,0/32/65"`;
		} else {
			// Power Nap, Meditation, Focus
			editItem.requestContent = `Start Time: 2025-04-26 19:15:34,
End Time: 2025-04-26 19:23:27,
Total sleep Time: 00:08,
User Age: 42
User Gender: Male
HRV(RMSSD) avg by 5-minute intervals: 101,
Heart Rate min/avg/max by 5-minute intervals: 64/68/72,62/68/80,64/66/70`;
		}

		modalTitle = "AI 리포트 프롬프트 신규 등록";
		showModal = true;
		setFocus("systemMessageKo");
	};

	const fnProcessEdit = (item) => {
		editItem = { ...item };

		modalTitle = "AI 리포트 프롬프트 정보 편집";
		showModal = true;
		setFocus("systemMessageKo");
	};

	const fnProcessView = (item) => {
		viewItem = { ...item };
		viewModalTitle = "AI 리포트 프롬프트 상세보기";
		showViewModal = true;
	};

	const fnDelete = async (id) => {
		const item = data.find((item) => item.id === id);
		if (item.isActive) {
			alert("사용 중인 프롬프트는 삭제할 수 없습니다.");
			return;
		}

		if (confirm("정말 삭제하시겠습니까?")) {
			const success = await del(`/ai-report-prompt/${id}`);
			if (success) {
				alert("프롬프트가 삭제되었습니다.");
				await fnSearch();
			}
		}
	};

	const closeModal = () => {
		showModal = false;
		resetEdit();
	};

	const closeViewModal = () => {
		showViewModal = false;
	};

	const resetViewModal = () => {
		viewItem = { ...defaultEditItem };
	};

	// 필터링된 데이터를 반환하는 함수
	const getFilteredData = (data) => {
		if (!data) return [];
		if (activeTab === "all") return data;

		const typeValue = TYPES[activeTab]?.value;
		return data.filter((item) => item.type === typeValue);
	};

	// 탭 변경 핸들러
	const handleTabChange = (tab) => {
		activeTab = tab;
		fnSearch();
	};

	const handlePageSizeChange = (event) => {
		pageSize = parseInt(event.target.value);
		currentPage = 1;
		fnSearch();
	};

	const handlePageChange = (page) => {
		if (page < 1 || page > totalPages) return;
		currentPage = page;
		fnSearch();
	};

	const fnRegistServer = async () => {
		if (confirm("서버에 적용하시겠습니까?")) {
			try {
				const success = await refreshAiReportPromptCache();
				if (success) {
					alert("서버 적용이 완료되었습니다.");
					await fnSearch();
				}
			} catch (error) {
				console.error("서버 적용 실패:", error);
				alert("서버 적용에 실패했습니다.");
			}
		}
	};

	$: {
		filteredData = getFilteredData(data || []);
	}

	const tabs = [{ key: "all", text: "전체 프롬프트" }, ...Object.entries(TYPES).map(([key, type]) => ({ key, text: type.text }))];

	// AI 테스트 질의 관련 상태
	const LANG_OPTIONS = [
		{ value: "Ko", label: "한국어" },
		{ value: "En", label: "영어" },
		{ value: "Ja", label: "일본어" },
		{ value: "ZhCn", label: "중국어간체" },
		{ value: "ZhTw", label: "중국어번체" },
	];

	const ENGINES = [
		{ value: 1, label: "GPT-4o Mini" },
		{ value: 2, label: "GPT-4o" },
		{ value: 3, label: "GPT-3.5 Turbo" },
	];

	let selectedLang = "Ko";
	let selectedEngine = 2; // 기본값: GPT-4o
	let temperature = 0.7;
	let aiResponse = "";
	let isQuerying = false;
	let isTyping = false;
	let typedResponse = "";
	let typingSpeed = 30; // ms per character

	// AI 테스트 질의용 반응형 변수들
	let currentSystemMessage = "";
	let currentResponseRules = "";
	let currentLengthInstruction = "";

	function getLangField(base) {
		return base + selectedLang;
	}

	// 타이핑 효과 함수
	async function typeResponse(text) {
		isTyping = true;
		typedResponse = "";

		for (let i = 0; i < text.length; i++) {
			typedResponse += text[i];
			await new Promise((resolve) => setTimeout(resolve, typingSpeed));
		}

		isTyping = false;
	}

	async function handleAiQuery() {
		isQuerying = true;
		aiResponse = "";

		try {
			// 편집 모달인지 상세보기 모달인지 확인
			const isEditMode = showModal;
			const currentItem = isEditMode ? editItem : viewItem;

			// 현재 선택된 언어의 프롬프트 정보 가져오기
			const systemMessage = currentItem[getLangField("systemMessage")] || "";
			const responseRules = currentItem[getLangField("responseRules")] || "";
			const lengthInstruction = currentItem[getLangField("lengthInstruction")] || "";

			// DB에서 가져온 엔진과 창의성 값 사용
			const dbEngine = currentItem.engine || 2;
			const dbCreativity = currentItem.creativity || 0.7;
			const dbRequestContent = currentItem.requestContent || "";

			// 엔진 ID를 OpenAI 모델명으로 변환
			const engineMap = {
				1: "gpt-4o-mini",
				2: "gpt-4o",
				3: "gpt-3.5-turbo",
			};
			const modelName = engineMap[dbEngine] || "gpt-4o";

			// OpenAI API 요청 데이터 구성
			const payload = {
				model: modelName,
				messages: [
					{ role: "system", content: systemMessage },
					{ role: "system", content: responseRules },
					{ role: "system", content: lengthInstruction },
					{ role: "user", content: dbRequestContent },
				],
				max_tokens: 1000,
				temperature: dbCreativity,
			};
			// API 호출
			const response = await post("/ai-report/chat/completion", payload);

			if (response.resultCode !== 1000) {
				throw new Error(`API 요청 실패: ${response.resultDescKo}`);
			}

			const result = await response.data;

			if (result && result.length > 0) {
				aiResponse = result;
				// 타이핑 효과 시작
				await typeResponse(result);
			} else {
				aiResponse = "AI 응답을 받지 못했습니다.";
				await typeResponse(aiResponse);
			}
		} catch (error) {
			console.error("AI 질의 실패:", error);
			aiResponse = `오류가 발생했습니다: ${error.message}`;
		} finally {
			isQuerying = false;
		}
	}

	onMount(() => {
		menu.set("컨텐츠 관리");
		menuSub.set("AI 리포트 관리");
		menuSubSub.set("AI 리포트 프롬프트");
		fnSearch();
	});

	// selectedLang 또는 viewItem 변경 시 반응형 변수 업데이트
	$: {
		if (selectedLang && viewItem) {
			currentSystemMessage = viewItem[getLangField("systemMessage")];
			currentResponseRules = viewItem[getLangField("responseRules")];
			currentLengthInstruction = viewItem[getLangField("lengthInstruction")];
		}
	}
</script>

<div class="card mb-4">
	<div class="table-responsive text-nowrap">
		<Tab {tabs} {activeTab} onTabChange={handleTabChange} />
		<div class="ms-auto" style="padding: 5px;">
			<TableToolbar
				{totalItems}
				{currentPage}
				{totalPages}
				{pageSize}
				pageSizes={PAGE_SIZES}
				onPageSizeChange={handlePageSizeChange}
				onReload={fnSearch}
				onRegist={fnProcessRegist}
				onRegistServer={fnRegistServer}
			/>
		</div>
		<div class="fade-container {activeTab ? 'show' : ''}">
			<!-- 메인 테이블 -->
			<table class="table small">
				<thead>
					<tr style="text-align:center">
						<th width="80">No</th>
						<th width="150">분류</th>
						<th width="200">시스템 메시지</th>
						<th width="*">응답 규칙</th>
						<th width="150">길이 지침</th>
						<th width="80">사용여부</th>
						<th width="120">등록일시</th>
						<th width="120">수정일시</th>
						<th width="100">관리</th>
					</tr>
				</thead>
				<tbody>
					{#each filteredData as item, index}
						<tr style="text-align:center">
							<td>{index + 1}</td>
							<td><b>{TYPES[Object.keys(TYPES).find((key) => TYPES[key].value === item.type)]?.text}</b></td>
							<td style="text-align: left; max-width: 200px; white-space: pre-wrap; word-wrap: break-word;">
								<span
									style="color: #0d6efd; cursor: pointer; text-decoration: underline;"
									on:click={() => fnProcessView(item)}
									on:keydown={(e) => e.key === "Enter" && fnProcessView(item)}
									role="button"
									tabindex="0"
								>
									{item.systemMessageKo}
								</span>
							</td>
							<td style="text-align: left; max-width: 200px; white-space: pre-wrap; word-wrap: break-word;">
								<span
									style="color: #0d6efd; cursor: pointer; text-decoration: underline;"
									on:click={() => fnProcessView(item)}
									on:keydown={(e) => e.key === "Enter" && fnProcessView(item)}
									role="button"
									tabindex="0"
								>
									{item.responseRulesKo}
								</span>
							</td>
							<td style="text-align: left; max-width: 200px; white-space: pre-wrap; word-wrap: break-word;">
								<span
									style="color: #0d6efd; cursor: pointer; text-decoration: underline;"
									on:click={() => fnProcessView(item)}
									on:keydown={(e) => e.key === "Enter" && fnProcessView(item)}
									role="button"
									tabindex="0"
								>
									{item.lengthInstructionKo}
								</span>
							</td>
							<td>
								<div class="form-check form-switch d-flex justify-content-center">
									<input class="form-check-input" type="checkbox" checked={item.isActive} disabled />
								</div>
							</td>
							<td>{Dates.defaultConvertFull(item.createdAt)}</td>
							<td>{Dates.defaultConvertFull(item.updatedAt)}</td>
							<td>
								<button class="btn btn-sm btn-outline-primary me-1" on:click={() => fnProcessEdit(item)}>편집</button>
								<button class="btn btn-sm btn-outline-primary" on:click={() => fnDelete(item.id)}>삭제</button>
							</td>
						</tr>
					{/each}
				</tbody>
			</table>
		</div>
		<Pagination {currentPage} {totalPages} onPageChange={handlePageChange} />
	</div>
</div>

<!-- 모달 -->
{#if showModal}
	<div
		class="modal fade {showModal ? 'show' : ''}"
		style="display: block;"
		tabindex="-1"
		aria-modal="true"
		role="dialog"
		transition:fade={{ duration: 200 }}
	>
		<div class="modal-dialog modal-dialog-centered" style="max-width: 95vw; width: 95vw;">
			<div class="modal-content">
				<div class="modal-header bg-dark text-white">
					<h5 class="modal-title d-flex align-items-center" style="color: white !important;">
						<i class="bx bx-brain me-2"></i>
						{modalTitle}
					</h5>
					<button type="button" class="btn-close btn-close-white" on:click={closeModal}></button>
				</div>
				<div class="modal-body d-flex flex-row" style="max-height: 80vh; overflow-y: auto;">
					<!-- 좌측: 편집 영역 -->
					<div class="flex-grow-1" style="min-width:0;max-width:60vw;padding-right:1.5rem;">
						<!-- 상단: 기본 정보 -->
						<div class="mb-4">
							<h6 class="border-bottom pb-2 mb-3 d-flex align-items-center bg-light p-2 rounded">
								<i class="bx bx-info-circle me-2"></i>
								기본 정보
							</h6>
							<div class="row text-center info-row">
								<div class="col">
									<span class="info-label">분류</span>
									<span class="info-value">
										{TYPES[Object.keys(TYPES).find((key) => TYPES[key].value === (editItem?.type ?? viewItem?.type))]?.text}
									</span>
								</div>
								<div class="col">
									<span class="info-label">사용여부</span>
									<span class="info-value">
										<div class="form-check form-switch d-flex justify-content-center">
											<input
												class="form-check-input"
												type="checkbox"
												checked={editItem.isActive}
												on:change={(e) => (editItem.isActive = e.target.checked)}
											/>
										</div>
									</span>
								</div>
								<div class="col">
									<span class="info-label">등록일시</span>
									<span class="info-value">{Dates.defaultConvertFull(editItem?.createdAt ?? viewItem?.createdAt)}</span>
								</div>
								<div class="col">
									<span class="info-label">수정일시</span>
									<span class="info-value">{Dates.defaultConvertFull(editItem?.updatedAt ?? viewItem?.updatedAt)}</span>
								</div>
							</div>
						</div>

						<!-- 프롬프트 내용 -->
						<div>
							<!-- 시스템 메시지 섹션 -->
							<div class="mb-4">
								<h6 class="border-bottom pb-2 mb-3 d-flex align-items-center bg-light p-2 rounded">
									<i class="bx bx-message-square me-2"></i>
									시스템 메시지
								</h6>
								<div class="row">
									{#each LANGUAGES as { field, label }, index}
										<div class="col-md-6 mb-3">
											<label for={field} class="form-label fw-bold">{label}</label>
											<input
												id={field}
												type="text"
												class="form-control form-control-sm"
												bind:value={editItem[field]}
												placeholder="시스템 메시지를 입력하세요"
											/>
										</div>
									{/each}
								</div>
							</div>

							<!-- 응답 규칙 섹션 -->
							<div class="mb-4">
								<h6 class="border-bottom pb-2 mb-3 d-flex align-items-center bg-light p-2 rounded">
									<i class="bx bx-list-check me-2"></i>
									응답 규칙
								</h6>
								<div class="row">
									{#each RESPONSE_RULES as { field, label }, index}
										<div class="col-md-6 mb-3">
											<label for={field} class="form-label fw-bold">{label}</label>
											<textarea
												id={field}
												class="form-control form-control-sm"
												rows="3"
												bind:value={editItem[field]}
												placeholder="응답 규칙을 입력하세요"
											></textarea>
										</div>
									{/each}
								</div>
							</div>

							<!-- 길이 지침 섹션 -->
							<div class="mb-4">
								<h6 class="border-bottom pb-2 mb-3 d-flex align-items-center bg-light p-2 rounded">
									<i class="bx bx-ruler me-2"></i>
									길이 지침
								</h6>
								<div class="row">
									{#each LENGTH_INSTRUCTIONS as { field, label }, index}
										<div class="col-md-6 mb-3">
											<label for={field} class="form-label fw-bold">{label}</label>
											<input
												id={field}
												type="text"
												class="form-control form-control-sm"
												bind:value={editItem[field]}
												placeholder="길이 지침을 입력하세요"
											/>
										</div>
									{/each}
								</div>
							</div>
						</div>
					</div>

					<!-- 우측: AI 테스트 질의 패널 -->
					<div class="ai-query-panel flex-grow-1 ps-4" style="min-width:340px;max-width:520px;border-left:1px solid #e9ecef;">
						<h6 class="border-bottom pb-2 mb-3 d-flex align-items-center bg-light p-2 rounded">
							<i class="bx bx-bot me-2"></i>
							AI 테스트 질의
						</h6>
						<div class="mb-3">
							<div class="d-flex align-items-baseline mb-2">
								<span class="form-label fw-bold form-label-sm me-2 mb-0">질의언어</span>
								<select id="edit-ai-lang-select" class="form-select form-select-sm form-control-sm w-auto" bind:value={selectedLang}>
									{#each LANG_OPTIONS as opt}
										<option value={opt.value}>{opt.label}</option>
									{/each}
								</select>
								<span style="width: 12px;"></span>
								<span class="form-label fw-bold form-label-sm me-2 mb-0">AI 엔진</span>
								<select
									id="edit-ai-engine-select"
									class="form-select form-select-sm form-control-sm w-auto"
									bind:value={editItem.engine}
								>
									{#each ENGINES as opt}
										<option value={opt.value}>{opt.label}</option>
									{/each}
								</select>
							</div>
							<div class="d-flex align-items-baseline">
								<span class="form-label fw-bold form-label-sm me-2 mb-0">AI 창의성</span>
								<input
									type="range"
									class="form-range form-range-sm"
									min="0.1"
									max="1"
									step="0.1"
									bind:value={editItem.creativity}
									style="width: 100px;"
								/>
								<span class="ms-2 text-muted" style="font-size: 12px; min-width: 30px;">{editItem.creativity}</span>
							</div>
						</div>
						<div class="mb-3">
							<span class="form-label fw-bold form-label-sm">시스템 메시지</span>
							<div class="border rounded p-2 bg-light form-control-sm" style="overflow-wrap:break-word;word-break:break-all;">
								{editItem[getLangField("systemMessage")] || "내용 없음"}
							</div>
						</div>
						<div class="mb-3">
							<span class="form-label fw-bold form-label-sm">응답 규칙</span>
							<div
								class="border rounded p-2 bg-light form-control-sm"
								style="overflow-wrap:break-word;word-break:break-all;white-space:pre-wrap;"
							>
								{editItem[getLangField("responseRules")] || "내용 없음"}
							</div>
						</div>
						<div class="mb-3">
							<span class="form-label fw-bold form-label-sm">길이 지침</span>
							<div class="border rounded p-2 bg-light form-control-sm" style="overflow-wrap:break-word;word-break:break-all;">
								{editItem[getLangField("lengthInstruction")] || "내용 없음"}
							</div>
						</div>
						<div class="mb-3">
							<span class="form-label fw-bold form-label-sm">AI 요청 본문</span>
							<textarea
								class="form-control form-control-sm"
								rows="8"
								bind:value={editItem.requestContent}
								placeholder="AI에게 전달할 요청 내용을 입력하세요"
								style="font-size: 12px; line-height: 1.4;"
								maxlength="5000"
							></textarea>
							<small class="text-muted">현재 {editItem.requestContent?.length || 0}/5000자</small>
						</div>
						<button class="btn btn-primary btn-sm w-100" on:click={handleAiQuery} disabled={isQuerying}>
							{#if isQuerying}
								<span class="spinner-border spinner-border-sm me-2" role="status"></span>
								AI에게 질의 중...
							{:else}
								AI에게 질의
							{/if}
						</button>
						<div class="mt-4">
							<span class="form-label fw-bold form-label-sm">AI 응답</span>
							<div
								class="border rounded p-2 bg-light form-control-sm"
								style="overflow-wrap:break-word;word-break:break-all;white-space:pre-wrap;min-height:80px;max-height:400px;overflow-y:auto;"
							>
								{#if isQuerying}
									<div class="text-muted">AI 응답을 기다리는 중...</div>
								{:else if isTyping}
									<span class="typing-text">
										{typedResponse}
										<span class="typing-cursor">|</span>
									</span>
								{:else if aiResponse}
									{aiResponse}
								{:else}
									<div class="text-muted">아직 질의가 없습니다.</div>
								{/if}
							</div>
						</div>
					</div>
				</div>
				<div class="modal-footer">
					<button type="button" class="btn btn-secondary btn-sm" on:click={closeModal}>취소</button>
					<button type="button" class="btn btn-primary btn-sm" on:click={handleSave} disabled={isSaving}>
						{#if isSaving}
							<span class="spinner-border spinner-border-sm me-2" role="status"></span>
							저장 중...
						{:else}
							저장
						{/if}
					</button>
				</div>
			</div>
		</div>
	</div>
	<div class="modal-backdrop fade show"></div>
{/if}

<!-- 상세보기 모달 -->
{#if showViewModal}
	<div
		class="modal fade {showViewModal ? 'show' : ''}"
		style="display: block;"
		tabindex="-1"
		aria-modal="true"
		role="dialog"
		transition:fade={{ duration: 200 }}
	>
		<div class="modal-dialog modal-dialog-centered" style="max-width: 95vw; width: 95vw;">
			<div class="modal-content">
				<div class="modal-header bg-dark text-white">
					<h5 class="modal-title d-flex align-items-center" style="color: white !important;">
						<i class="bx bx-search me-2"></i>
						{viewModalTitle}
					</h5>
					<button type="button" class="btn-close btn-close-white" on:click={closeViewModal}></button>
				</div>
				<div class="modal-body d-flex flex-row" style="max-height: 80vh; overflow-y: auto;">
					<!-- 기존 상세보기 좌측 영역 -->
					<div class="flex-grow-1" style="min-width:0;max-width:60vw;padding-right:1.5rem;">
						<!-- 기존 상세보기 내용 시작 -->
						<!-- 상단: 기본 정보 -->
						<div class="mb-4">
							<h6 class="border-bottom pb-2 mb-3 d-flex align-items-center bg-light p-2 rounded">
								<i class="bx bx-info-circle me-2"></i>
								기본 정보
							</h6>
							<div class="row text-center info-row">
								<div class="col">
									<span class="info-label">분류</span>
									<span class="info-value">
										{TYPES[Object.keys(TYPES).find((key) => TYPES[key].value === viewItem?.type)]?.text}
									</span>
								</div>
								<div class="col">
									<span class="info-label">사용여부</span>
									<span class="info-value">
										<span class="badge {viewItem.isActive ? 'bg-success' : 'bg-secondary'}">
											{viewItem.isActive ? "사용" : "미사용"}
										</span>
									</span>
								</div>
								<div class="col">
									<span class="info-label">등록일시</span>
									<span class="info-value">{Dates.defaultConvertFull(viewItem.createdAt)}</span>
								</div>
								<div class="col">
									<span class="info-label">수정일시</span>
									<span class="info-value">{Dates.defaultConvertFull(viewItem.updatedAt)}</span>
								</div>
							</div>
						</div>
						<!-- 프롬프트 내용 -->
						<div>
							<!-- 시스템 메시지 섹션 -->
							<div class="mb-4">
								<h6 class="border-bottom pb-2 mb-3 d-flex align-items-center bg-light p-2 rounded">
									<i class="bx bx-message-square me-2"></i>
									시스템 메시지
								</h6>
								<div class="row">
									{#each LANGUAGES as { field, label }, index}
										<div class="col-md-6 mb-3">
											<span class="form-label fw-bold form-label-sm">{label}</span>
											<div
												class="border rounded p-2 bg-light form-control-sm"
												style="overflow-wrap:break-word;word-break:break-all;"
											>
												{viewItem[field] || "내용 없음"}
											</div>
										</div>
									{/each}
								</div>
							</div>
							<!-- 응답 규칙 섹션 -->
							<div class="mb-4">
								<h6 class="border-bottom pb-2 mb-3 d-flex align-items-center bg-light p-2 rounded">
									<i class="bx bx-list-check me-2"></i>
									응답 규칙
								</h6>
								<div class="row">
									{#each RESPONSE_RULES as { field, label }, index}
										<div class="col-md-6 mb-3">
											<span class="form-label fw-bold form-label-sm">{label}</span>
											<div
												class="border rounded p-2 bg-light form-control-sm"
												style="overflow-wrap:break-word;word-break:break-all;white-space:pre-wrap;"
											>
												{viewItem[field] || "내용 없음"}
											</div>
										</div>
									{/each}
								</div>
							</div>
							<!-- 길이 지침 섹션 -->
							<div class="mb-4">
								<h6 class="border-bottom pb-2 mb-3 d-flex align-items-center bg-light p-2 rounded">
									<i class="bx bx-ruler me-2"></i>
									길이 지침
								</h6>
								<div class="row">
									{#each LENGTH_INSTRUCTIONS as { field, label }, index}
										<div class="col-md-6 mb-3">
											<span class="form-label fw-bold form-label-sm">{label}</span>
											<div
												class="border rounded p-2 bg-light form-control-sm"
												style="overflow-wrap:break-word;word-break:break-all;"
											>
												{viewItem[field] || "내용 없음"}
											</div>
										</div>
									{/each}
								</div>
							</div>
						</div>
						<!-- 기존 상세보기 내용 끝 -->
					</div>
					<!-- 우측: AI 테스트 질의 패널 -->
					<div class="ai-query-panel flex-grow-1 ps-4" style="min-width:340px;max-width:520px;border-left:1px solid #e9ecef;">
						<h6 class="border-bottom pb-2 mb-3 d-flex align-items-center bg-light p-2 rounded">
							<i class="bx bx-bot me-2"></i>
							AI 테스트 질의
						</h6>
						<div class="mb-3">
							<div class="d-flex align-items-baseline mb-2">
								<span class="form-label fw-bold form-label-sm me-2 mb-0">질의언어</span>
								<select id="ai-lang-select" class="form-select form-select-sm form-control-sm w-auto" bind:value={selectedLang}>
									{#each LANG_OPTIONS as opt}
										<option value={opt.value}>{opt.label}</option>
									{/each}
								</select>
								<span style="width: 12px;"></span>
								<span class="form-label fw-bold form-label-sm me-2 mb-0">AI 엔진</span>
								<span class="text-muted" style="font-size: 12px;">
									{ENGINES.find((e) => e.value === viewItem.engine)?.label || "GPT-4o"}
								</span>
							</div>
							<div class="d-flex align-items-baseline">
								<span class="form-label fw-bold form-label-sm me-2 mb-0">AI 창의성</span>
								<span class="text-muted" style="font-size: 12px;">{viewItem.creativity || 0.7}</span>
							</div>
						</div>
						<div class="mb-3">
							<span class="form-label fw-bold form-label-sm">시스템 메시지</span>
							<div class="border rounded p-2 bg-light form-control-sm" style="overflow-wrap:break-word;word-break:break-all;">
								{currentSystemMessage || "내용 없음"}
							</div>
						</div>
						<div class="mb-3">
							<span class="form-label fw-bold form-label-sm">응답 규칙</span>
							<div
								class="border rounded p-2 bg-light form-control-sm"
								style="overflow-wrap:break-word;word-break:break-all;white-space:pre-wrap;"
							>
								{currentResponseRules || "내용 없음"}
							</div>
						</div>
						<div class="mb-3">
							<span class="form-label fw-bold form-label-sm">길이 지침</span>
							<div class="border rounded p-2 bg-light form-control-sm" style="overflow-wrap:break-word;word-break:break-all;">
								{currentLengthInstruction || "내용 없음"}
							</div>
						</div>
						<div class="mb-3">
							<span class="form-label fw-bold form-label-sm">DB 요청 내용</span>
							<div
								class="border rounded p-2 bg-light form-control-sm"
								style="overflow-wrap:break-word;word-break:break-all;white-space:pre-wrap;min-height:80px;max-height:200px;overflow-y:auto;font-size: 12px; line-height: 1.4;"
							>
								{viewItem.requestContent || "내용 없음"}
							</div>
						</div>
						<button class="btn btn-primary btn-sm w-100" on:click={handleAiQuery} disabled={isQuerying}>
							{#if isQuerying}
								<span class="spinner-border spinner-border-sm me-2" role="status"></span>
								AI에게 질의 중...
							{:else}
								AI에게 질의
							{/if}
						</button>
						<div class="mt-4">
							<span class="form-label fw-bold form-label-sm">AI 응답</span>
							<div
								class="border rounded p-2 bg-light form-control-sm"
								style="overflow-wrap:break-word;word-break:break-all;white-space:pre-wrap;min-height:80px;max-height:400px;overflow-y:auto;"
							>
								{#if isQuerying}
									<div class="text-muted">AI 응답을 기다리는 중...</div>
								{:else if isTyping}
									<span class="typing-text">
										{typedResponse}
										<span class="typing-cursor">|</span>
									</span>
								{:else if aiResponse}
									{aiResponse}
								{:else}
									<div class="text-muted">아직 질의가 없습니다.</div>
								{/if}
							</div>
						</div>
					</div>
				</div>
				<div class="modal-footer">
					<button type="button" class="btn btn-secondary btn-sm" on:click={closeViewModal}>닫기</button>
					<button
						type="button"
						class="btn btn-primary btn-sm"
						on:click={() => {
							closeViewModal();
							setTimeout(() => fnProcessEdit(viewItem), 100);
						}}
					>
						편집
					</button>
				</div>
			</div>
		</div>
	</div>
	<div class="modal-backdrop fade show"></div>
{/if}
